import { MessageAppend, ToolInvocation } from '@bitte-ai/types';
import { memo } from 'react';
import { CodeBlock } from '../../CodeBlock';
import { ToolCallResult } from './ToolCallResult';

interface ToolCallProps {
  toolInvocation: ToolInvocation;
  hideToolCall?: boolean;
  showBorder?: boolean;
  messageId: string;
  groupKey: string;
  chatId: string;
  creator?: string;
  borderColor?: string;
  messageBackgroundColor?: string;
  textColor?: string;
  customApproveTxButton?: React.ComponentType<any>;
  customDeclineTxButton?: React.ComponentType<any>;
  customTxContainer?: React.ComponentType<any>;
  append?: MessageAppend;
  customToolComponents?: any[];
}

const ToolCallComponent = ({
  toolInvocation,
  hideToolCall,
  showBorder,
  messageId,
  groupKey,
  chatId,
  creator,
  borderColor,
  messageBackgroundColor,
  textColor,
  customApproveTxButton,
  customDeclineTxButton,
  customTxContainer,
  append,
  customToolComponents,
}: ToolCallProps) => {
  const { toolName, toolCallId, result } = toolInvocation;

  // Handle errors
  if (result?.error && !hideToolCall) {
    return (
      <div key={`${toolCallId}`} id={`tool-call-${toolName}-${toolCallId}`}>
        <CodeBlock content={`Error: ${result.error}`} />
      </div>
    );
  }

  // If we have a result, show it
  if (result) {
    return (
      <div key={`${toolCallId}`} id={`tool-call-${toolName}-${toolCallId}`}>
        <ToolCallResult
          toolName={toolName}
          result={result}
          hideToolCall={hideToolCall}
          showBorder={showBorder}
          messageId={messageId}
          groupKey={groupKey}
          chatId={chatId}
          creator={creator}
          borderColor={borderColor}
          messageBackgroundColor={messageBackgroundColor}
          textColor={textColor}
          customApproveTxButton={customApproveTxButton}
          customDeclineTxButton={customDeclineTxButton}
          customTxContainer={customTxContainer}
          append={append}
          customToolComponents={customToolComponents}
        />
        {!hideToolCall && (
          <div className="bitte-mt-2 bitte-border-t" style={{ borderColor }} />
        )}
      </div>
    );
  }

  // No result yet - don't render anything, let ThinkingMessage handle loading
  return null;
};

// Optimized memo comparison
export const ToolCall = memo(ToolCallComponent, (prevProps, nextProps) => {
  // Quick reference check for toolInvocation object
  if (prevProps.toolInvocation === nextProps.toolInvocation) {
    return true;
  }

  // Check if the essential properties have changed
  const prevTool = prevProps.toolInvocation;
  const nextTool = nextProps.toolInvocation;

  // These are the most critical props that affect rendering
  if (prevTool.toolCallId !== nextTool.toolCallId) return false;
  if (prevTool.toolName !== nextTool.toolName) return false;
  if (prevTool.state !== nextTool.state) return false;
  if (prevTool.result !== nextTool.result) return false;

  // Check other essential props
  if (prevProps.messageId !== nextProps.messageId) return false;
  if (prevProps.groupKey !== nextProps.groupKey) return false;

  // Only check these if they actually change the visual output
  if (prevProps.hideToolCall !== nextProps.hideToolCall) return false;
  if (prevProps.showBorder !== nextProps.showBorder) return false;

  return true;
});

ToolCall.displayName = 'ToolCall';
